import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import {
  currentDocumentLocale,
  defaultLocale,
  isRtlLocale,
  normalizeLocale,
  type SupportedLocale
} from "./preferences";

export type LocaleNamespace = "common" | "webmaster" | "advertiser" | "admin";

type LocaleLoader = () => Promise<{ default: Record<string, unknown> }>;

const resourceLoaders: Record<SupportedLocale, Record<LocaleNamespace, LocaleLoader>> = {
  en: {
    common: () => import("../locales/en/common.json"),
    webmaster: () => import("../locales/en/webmaster.json"),
    advertiser: () => import("../locales/en/advertiser.json"),
    admin: () => import("../locales/en/admin.json")
  },
  ru: {
    common: () => import("../locales/ru/common.json"),
    webmaster: () => import("../locales/ru/webmaster.json"),
    advertiser: () => import("../locales/ru/advertiser.json"),
    admin: () => import("../locales/ru/admin.json")
  },
  "zh-CN": {
    common: () => import("../locales/zh-CN/common.json"),
    webmaster: () => import("../locales/zh-CN/webmaster.json"),
    advertiser: () => import("../locales/zh-CN/advertiser.json"),
    admin: () => import("../locales/zh-CN/admin.json")
  }
};

export async function loadLocaleNamespaces(locale: SupportedLocale, namespaces: readonly LocaleNamespace[]): Promise<void> {
  if (!i18n.isInitialized) {
    await initializeI18n(locale);
  }

  await Promise.all(
    namespaces.map(async (namespace) => {
      if (i18n.hasResourceBundle(locale, namespace)) {
        return;
      }

      const mod = await resourceLoaders[locale][namespace]();
      i18n.addResourceBundle(locale, namespace, mod.default, true, true);
    })
  );
}

export async function changeLocale(locale: SupportedLocale): Promise<void> {
  await loadLocaleNamespaces(locale, ["common"]);
  await i18n.changeLanguage(locale);

  const root = document.documentElement;
  root.lang = locale;
  root.dir = isRtlLocale(locale) ? "rtl" : "ltr";
}

export async function initializeI18n(initialLocale = currentDocumentLocale()): Promise<typeof i18n> {
  const locale = normalizeLocale(initialLocale) ?? defaultLocale;
  if (!i18n.isInitialized) {
    await i18n.use(initReactI18next).init({
      lng: locale,
      fallbackLng: defaultLocale,
      supportedLngs: Object.keys(resourceLoaders),
      defaultNS: "common",
      ns: ["common"],
      resources: {},
      partialBundledLanguages: true,
      interpolation: {
        escapeValue: false
      },
      returnNull: false
    });
  } else {
    await i18n.changeLanguage(locale);
  }

  await loadLocaleNamespaces(defaultLocale, ["common"]);
  await loadLocaleNamespaces(locale, ["common"]);
  await i18n.changeLanguage(locale);

  document.documentElement.lang = locale;
  document.documentElement.dir = isRtlLocale(locale) ? "rtl" : "ltr";

  return i18n;
}

export { i18n };
